import React, { FormEvent, useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { IconButton, LinearProgress, Stack, StackProps, TextFieldProps, Typography } from '@mui/material';

import { useRouter } from 'next/router';
import zxcvbn from 'zxcvbn';

import { ROUTES } from '@arrived/common';
import {
  Auth0SignupAccountCreationEventProperties,
  FormTextField,
  useAuth0Signup,
  useEmailHelperText,
  usePasswordHelperText,
  usePasswordStrength,
  usePasswordStrengthColor,
} from '@arrived/components';
import { CONFIG } from '@arrived/config';
import { SignupSchema } from '@arrived/forms';

export interface SignupFormProps extends Omit<StackProps, 'children'> {
  accountCreationEventProperties?: Auth0SignupAccountCreationEventProperties;
  EmailFormTextFieldProps?: TextFieldProps;
  LoadingButtonProps?: LoadingButtonProps;
  PasswordFormTextFieldProps?: TextFieldProps;
  useFormReturn: UseFormReturn<SignupSchema>;
}

export const SignupForm = ({
  accountCreationEventProperties,
  EmailFormTextFieldProps,
  LoadingButtonProps,
  PasswordFormTextFieldProps,
  useFormReturn,
  ...rest
}: SignupFormProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();

  const { InputProps: PasswordFormTextFieldInputProps, ...PasswordFormTextFieldRest } =
    PasswordFormTextFieldProps || {};
  const { sx: LoadingButtonSx, ...LoadingButtonRest } = LoadingButtonProps || {};

  const { control, formState, handleSubmit, getFieldState, watch } = useFormReturn;

  const { errors, isSubmitting, isValid } = formState;
  const { isDirty: passwordIsDirty } = getFieldState('password');
  const password = watch('password') ?? '';

  const onLoginError = useCallback(() => router.push(ROUTES.logIn), [router]);

  const { auth0Error, signupAndLogin } = useAuth0Signup({
    accountCreationEventProperties,
    onLoginError,
    redirectUri: () => window.location.origin + CONFIG.baseHref + ROUTES.register,
  });

  const toggleShowPassword = useCallback(() => setShowPassword((showPassword) => !showPassword), []);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      // Prevents the default action to append the form data to the URL
      event.preventDefault();
      return handleSubmit(signupAndLogin)();
    },
    [handleSubmit, signupAndLogin]
  );

  const emailHelperText = useEmailHelperText(auth0Error);

  const { feedback, score } = useMemo(() => zxcvbn(password), [password]);

  const passwordStrength = usePasswordStrength(score, password);

  const passwordHelperText = usePasswordHelperText({
    auth0Error,
    hasMetPasswordRequirements: !errors.password,
    passwordStrength,
    zxcvbnFeedback: feedback,
  });

  const passwordStrengthColor = usePasswordStrengthColor(passwordStrength);

  // Allows us to style the form directly as a Stack without needing separate DOM elements and spreading the styles
  // amongst separate components.
  const StackFormComponent = useCallback(
    React.forwardRef((props: any, ref) => <form ref={ref} {...props} />),
    []
  );

  return (
    <Stack component={StackFormComponent} onSubmit={onSubmit} {...rest}>
      <FormTextField
        autoComplete="username"
        controllerProps={{ control, name: 'email' }}
        error={Boolean(errors.email || emailHelperText)}
        label={<FormattedMessage id="email" defaultMessage="Email" />}
        type="email"
        required
        {...EmailFormTextFieldProps}
      />
      {emailHelperText && (
        <Typography variant="body1" color="error">
          {emailHelperText}
        </Typography>
      )}
      <FormTextField
        autoComplete="new-password"
        controllerProps={{ control, name: 'password' }}
        label={<FormattedMessage id="password" defaultMessage="Password" />}
        type={!showPassword ? 'password' : undefined}
        required
        InputProps={{
          endAdornment: (
            <IconButton onClick={toggleShowPassword}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
          ),
          ...PasswordFormTextFieldInputProps,
        }}
        {...PasswordFormTextFieldRest}
      />
      {passwordIsDirty && (
        <LinearProgress
          color={passwordStrengthColor}
          variant="determinate"
          value={(passwordStrength * 100) / 6}
          sx={{ backgroundColor: 'background.info' }}
        />
      )}
      {passwordHelperText && passwordIsDirty && (
        <Typography variant="body2" color={passwordStrength < 2 || errors.password ? 'error' : 'font.600'}>
          {passwordHelperText}
        </Typography>
      )}
      <LoadingButton
        disabled={!isValid || passwordStrength < 2}
        loading={isSubmitting}
        fullWidth
        variant="contained"
        sx={{
          maxWidth: 'unset',
          '&:not(.Mui-disabled)': {
            background: ({ gradients }) => gradients.sea,
          },
          ...LoadingButtonSx,
        }}
        type="submit"
        {...LoadingButtonRest}
      />
    </Stack>
  );
};
